import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Oval } from "react-loader-spinner";
import { FaPlus, FaEdit, FaTrash, FaCheck, FaTimes } from "react-icons/fa";
import { StandardURL } from '../services/constants';

const PromoCodesPage = () => {
  const [promoCodes, setPromoCodes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState("add"); // "add" or "update"
  const [modalData, setModalData] = useState({
    code: "",
    amount: "",
    expiry_date: "",
    usage_limit: "",
  });

  useEffect(() => {
    fetchPromoCodes();
  }, []);

  const fetchPromoCodes = async () => {
    setIsLoading(true);
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(`${StandardURL}/iMusic/promo-codes/fetchall`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setPromoCodes(response.data.response);
    } catch (error) {
      console.error("Error fetching promo codes:", error);
      toast.error(error.response?.data?.response || "Failed to fetch promo codes.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAction = async () => {
    const { code, amount, expiry_date, usage_limit } = modalData;
    if (!code || !amount || !expiry_date || !usage_limit) {
      toast.error("All fields are required.");
      return;
    }

    setIsActionLoading(true);
    const token = localStorage.getItem("token");
    const endpoint = modalAction === "add" ? `${StandardURL}/iMusic/promo-codes/add` : `${StandardURL}/iMusic/promo-codes/update/${modalData.promo_code_id}`;
    const method = modalAction === "add" ? "post" : "patch";

    try {
      const response = await axios({
        method: method,
        url: endpoint,
        data: {
          code,
          amount,
          expiry_date,
          usage_limit,
        },
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      toast.success(response.data.response);
      closeModal();
      fetchPromoCodes();
    } catch (error) {
      console.error(`Error ${modalAction}ing promo code:`, error);
      toast.error(error.response?.data?.response || `Failed to ${modalAction} promo code.`);
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleDeletePromoCode = async (promoCodeId) => {
    if (!window.confirm("Are you sure you want to delete this promo code? This action cannot be undone.")) {
      return;
    }

    setIsActionLoading(true);
    const token = localStorage.getItem("token");
    try {
      const response = await axios.delete(
        `${StandardURL}/iMusic/promo-codes/delete/${promoCodeId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success(response.data.response);
      fetchPromoCodes();
    } catch (error) {
      console.error("Error deleting promo code:", error);
      toast.error(error.response?.data?.response || "Failed to delete promo code.");
    } finally {
      setIsActionLoading(false);
    }
  };

  const openModal = (action, promoCode = null) => {
    setModalAction(action);
    setModalData(
      promoCode
        ? { ...promoCode, expiry_date: promoCode.expiry_date.split('T')[0] }
        : { code: "", amount: "", expiry_date: "", usage_limit: "" }
    );
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalData({ code: "", amount: "", expiry_date: "", usage_limit: "" });
  };

  const handleModalInputChange = (e) => {
    const { name, value } = e.target;
    setModalData({ ...modalData, [name]: value });
  };

  return (
    <>
      <style>{styles}</style>
      <div className="admin-wrapper">
        <div className="admin-container">
          
          {/* Header Card */}
          <div className="admin-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "2rem" }}>
            <h1 className="admin-title" style={{ margin: 0 }}>
              Promo <span>Codes</span>
            </h1>
            <button
              onClick={() => openModal("add")}
              className="btn-submit"
              style={{ width: "auto", display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <FaPlus /> Add Promo Code
            </button>
          </div>

          {/* Table Card */}
          <div className="admin-card">
            {isLoading ? (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "3rem" }}>
                <Oval color="#e8ff3c" height={50} width={50} />
              </div>
            ) : promoCodes.length === 0 ? (
              <div style={{ textAlign: "center", padding: "3rem", color: "#666" }}>
                No promo codes available.
              </div>
            ) : (
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Amount</th>
                      <th>Expiry Date</th>
                      <th>Usage Limit</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {promoCodes.map((promoCode) => (
                      <tr key={promoCode.promo_code_id}>
                        <td className="accent-text" style={{ fontWeight: "600" }}>{promoCode.code}</td>
                        <td>USD {promoCode.amount}</td>
                        <td style={{ color: "#aaa" }}>
                          {new Date(promoCode.expiry_date).toLocaleDateString()}
                        </td>
                        <td>{promoCode.usage_limit}</td>
                        <td>
                          <div className="action-btns">
                            <button
                              onClick={() => openModal("update", promoCode)}
                              className="btn-action btn-edit"
                            >
                              <FaEdit style={{ marginRight: "4px" }} /> Update
                            </button>
                            <button
                              onClick={() => handleDeletePromoCode(promoCode.promo_code_id)}
                              disabled={isActionLoading}
                              className="btn-action btn-delete"
                            >
                              {isActionLoading ? (
                                <Oval height={12} width={12} color="#ff4545" />
                              ) : (
                                <><FaTrash style={{ marginRight: "4px" }} /> Delete</>
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Modal */}
        {isModalOpen && (
          <div className="modal-overlay">
            <div className="modal-content admin-form">
              <h2 className="admin-title" style={{ fontSize: "2rem", marginBottom: "1.5rem" }}>
                {modalAction === "add" ? "Add" : "Update"} <span>Promo Code</span>
              </h2>
              
              <div style={{ marginBottom: "1.2rem" }}>
                <label>Promo Code</label>
                <input
                  type="text"
                  name="code"
                  value={modalData.code}
                  onChange={handleModalInputChange}
                  placeholder="e.g. SUMMER2026"
                />
              </div>

              <div style={{ marginBottom: "1.2rem" }}>
                <label>Amount (USD)</label>
                <input
                  type="number"
                  name="amount"
                  value={modalData.amount}
                  onChange={handleModalInputChange}
                  placeholder="e.g. 500"
                />
              </div>

              <div style={{ marginBottom: "1.2rem" }}>
                <label>Expiry Date</label>
                <input
                  type="date"
                  name="expiry_date"
                  value={modalData.expiry_date}
                  onChange={handleModalInputChange}
                />
              </div>

              <div style={{ marginBottom: "1.5rem" }}>
                <label>Usage Limit</label>
                <input
                  type="number"
                  name="usage_limit"
                  value={modalData.usage_limit}
                  onChange={handleModalInputChange}
                  placeholder="e.g. 100"
                />
              </div>

              <button
                onClick={handleAction}
                disabled={isActionLoading}
                className="btn-submit"
              >
                {isActionLoading ? (
                  <Oval height={20} width={20} color="#000" wrapperStyle={{ display: "flex", justifyContent: "center" }} />
                ) : (
                  <>{modalAction === "add" ? <FaCheck style={{ marginRight: "6px" }} /> : <FaEdit style={{ marginRight: "6px" }} />} {modalAction === "add" ? "Add Promo Code" : "Save Changes"}</>
                )}
              </button>

              <button
                onClick={closeModal}
                className="btn-cancel"
              >
                <FaTimes style={{ marginRight: "6px", display: "inline" }} /> Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

// Custom Styles Provided
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600&display=swap');

  .admin-wrapper {
    background: #0a0a0a;
    color: #f0f0f0;
    min-height: 100vh;
    padding: 3rem 2rem;
    font-family: 'DM Sans', sans-serif;
  }

  .admin-container {
    max-width: 1400px;
    margin: 0 auto;
  }

  .admin-card {
    background: #111111;
    border: 1px solid #2a2a2a;
    border-radius: 8px;
    padding: 2.5rem;
    margin-bottom: 2rem;
  }

  .admin-title {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 2.5rem;
    letter-spacing: 0.06em;
    margin-bottom: 1.5rem;
  }

  .admin-title span {
    color: #e8ff3c;
  }

  /* Form Styles */
  .admin-form label {
    display: block;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: #666;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  .admin-form input,
  .admin-form select {
    width: 100%;
    background: #1a1a1a;
    border: 1px solid #2a2a2a;
    color: #f0f0f0;
    border-radius: 6px;
    padding: 0.75rem 1rem;
    font-size: 0.9rem;
    font-family: 'DM Sans', sans-serif;
    outline: none;
    transition: border-color 0.2s;
    box-sizing: border-box;
  }

  .admin-form input:focus,
  .admin-form select:focus {
    border-color: #e8ff3c;
  }

  .btn-submit {
    background: #e8ff3c;
    color: #000;
    border: none;
    border-radius: 4px;
    padding: 0.85rem 2rem;
    font-family: 'DM Sans', sans-serif;
    font-weight: 600;
    font-size: 0.95rem;
    cursor: pointer;
    transition: opacity 0.2s;
    width: 100%;
  }

  .btn-submit:hover:not(:disabled) {
    opacity: 0.85;
  }

  .btn-submit:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Table Styles */
  .admin-table-wrap {
    overflow-x: auto;
  }

  .admin-table {
    width: 100%;
    border-collapse: collapse;
    text-align: left;
    white-space: nowrap;
  }

  .admin-table th {
    padding: 1rem;
    border-bottom: 1px solid #2a2a2a;
    color: #666;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .admin-table td {
    padding: 1rem;
    border-bottom: 1px solid #1a1a1a;
    vertical-align: middle;
    font-size: 0.9rem;
  }

  .admin-table tr:hover {
    background: rgba(255, 255, 255, 0.02);
  }

  .accent-text {
    color: #e8ff3c;
    font-family: monospace;
  }

  .action-btns {
    display: flex;
    gap: 0.5rem;
  }

  .btn-action {
    background: transparent;
    border: 1px solid;
    padding: 0.4rem 0.8rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.75rem;
    font-weight: 600;
    transition: all 0.2s;
  }

  .btn-edit {
    color: #4da6ff;
    border-color: rgba(77, 166, 255, 0.3);
  }

  .btn-edit:hover {
    background: rgba(77, 166, 255, 0.1);
    border-color: #4da6ff;
  }

  .btn-delete {
    color: #ff4545;
    border-color: rgba(255, 69, 69, 0.3);
  }

  .btn-delete:hover {
    background: rgba(255, 69, 69, 0.1);
    border-color: #ff4545;
  }

  /* Modal */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 1rem;
  }

  .modal-content {
    background: #111;
    border: 1px solid #2a2a2a;
    border-radius: 8px;
    width: 100%;
    max-width: 500px;
    padding: 2.5rem;
    box-shadow: 0 10px 40px rgba(0,0,0,0.5);
    max-height: 90vh;
    overflow-y: auto;
  }

  .btn-cancel {
    background: transparent;
    color: #f0f0f0;
    border: 1px solid #666;
    border-radius: 4px;
    padding: 0.85rem 2rem;
    cursor: pointer;
    width: 100%;
    margin-top: 1rem;
  }

  .btn-cancel:hover {
    background: rgba(255,255,255,0.05);
  }

  @media (max-width: 768px) {
    .admin-wrapper {
      padding: 1.5rem 1rem;
    }
    .admin-card {
      padding: 1.5rem;
    }
  }
`;

export default PromoCodesPage;